import type { EntryKey } from "@pinia/colada";
import type { MaybeRefOrGetter } from "vue";
import type { ChargeSubtype } from "@/utils/charge-types";
import { useQuery, useQueryCache } from "@pinia/colada";
import { toValue } from "vue";
import { api } from "@/services/api";
import { useProjectStore } from "@/stores/project.store";

/**
 * Derived from the centralized charge subtype registry. Do not duplicate the
 * union here: a new subtype must flow through the dashboard without edits.
 */
export type ChargeSubType = ChargeSubtype;

export const UNITS = [
  "token",
  "character",
  "second",
  "minute",
  "hour",
  "month",
  "request",
  "image",
  "byte",
  "kilobyte",
  "megabyte",
  "gigabyte",
] as const;

/**
 * Parameters for usage by type query
 */
export interface UsageByTypeParams {
  start_date: string;
  end_date: string;
}

/**
 * Response type for usage by type endpoint
 */
export interface CostPerType {
  date: string;
  total_cost: number;
  total_usage: number;
  total_requests: number;
  type: ChargeSubType;
  unit: (typeof UNITS)[number];
  currency: string;
  pricing_rule: string;
  identifier: string;
}

/**
 * Response type for dashboard KPIs endpoint
 */
export interface DashboardKPIs {
  total_cost: number;
  total_requests: number;
  total_tokens: number;
  unique_models: number;
  total_conversations: number;
  total_transcription_seconds: number;
}

/**
 * Query keys factory for dashboard following Pinia Colada best practices
 *
 * @see https://pinia-colada.esm.dev/guide/query-keys.html
 */
export const DASHBOARD_QUERY_KEYS = {
  root: ["dashboard"] as const,
  usageByType: (projectId: string | null | undefined, params: UsageByTypeParams) =>
    [...DASHBOARD_QUERY_KEYS.root, "usage-by-type", { projectId }, params] as const,
  kpis: (projectId: string | null | undefined, params: UsageByTypeParams) =>
    [...DASHBOARD_QUERY_KEYS.root, "kpis", { projectId }, params] as const,
};

/**
 * Query hook for fetching usage data grouped by type
 *
 * Features:
 * - Smart caching with 30s stale time
 * - Automatic deduplication of concurrent requests
 * - Placeholder data (keeps previous data while loading new)
 * - Reactive params (automatically refetches when dates change)
 *
 * @param params - Reactive date range parameters
 *
 * @example
 * ```ts
 * const params = computed(() => ({
 *   start_date: startDate.value.toISOString(),
 *   end_date: endDate.value.toISOString(),
 * }));
 *
 * const usageQuery = useUsageByTypeQuery(params);
 *
 * // In template
 * if (usageQuery.isLoading.value) return <Spinner />;
 * const usageData = usageQuery.data.value;
 * ```
 */
export function useUsageByTypeQuery(params: MaybeRefOrGetter<UsageByTypeParams>) {
  const projectStore = useProjectStore();

  return useQuery({
    // Include project ID in key so query refetches when project changes
    key: () =>
      DASHBOARD_QUERY_KEYS.usageByType(projectStore.currentProjectId, toValue(params)) as unknown as EntryKey,
    query: async () => {
      const { data } = await api.get<CostPerType[]>("/dashboard/usage-by-type", {
        params: toValue(params),
        // Pass project header to filter by selected project
        headers: projectStore.getProjectHeader(),
      });
      return data;
    },

    staleTime: 30_000, // 30 seconds - data is considered fresh
    gcTime: 5 * 60_000, // 5 minutes - garbage collection time
  });
}

/**
 * Query hook for fetching dashboard KPIs (project-scoped)
 */
export function useDashboardKPIsQuery(params: MaybeRefOrGetter<UsageByTypeParams>) {
  const projectStore = useProjectStore();

  return useQuery({
    key: () =>
      DASHBOARD_QUERY_KEYS.kpis(projectStore.currentProjectId, toValue(params)) as unknown as EntryKey,
    query: async () => {
      const { data } = await api.get<DashboardKPIs>("/dashboard/kpis", {
        params: toValue(params),
        headers: projectStore.getProjectHeader(),
      });
      return data;
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Helper hook to invalidate dashboard queries
 *
 * Use this after mutations that affect usage data or manual refresh
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateDashboard();
 *
 * // In refresh button handler
 * const handleRefresh = () => {
 *   invalidate.usageByType();
 * };
 * ```
 */
export function useInvalidateDashboard() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all dashboard queries
     */
    all: () => queryCache.invalidateQueries({ key: DASHBOARD_QUERY_KEYS.root as any }),

    /**
     * Invalidate only usage by type queries
     */
    usageByType: () =>
      queryCache.invalidateQueries({ key: [...DASHBOARD_QUERY_KEYS.root, "usage-by-type"] as any }),

    /**
     * Invalidate only KPI queries
     */
    kpis: () =>
      queryCache.invalidateQueries({ key: [...DASHBOARD_QUERY_KEYS.root, "kpis"] as any }),
  };
}
